import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/reduxStore';
import { clearCurrentChats, fetchConversationsThunk, getMessagesByConvoIdThunk } from 'modules/flywheel/store/chatStore';
import { useChatbot } from '../searchAgent/chatBotContext';
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "styled-components";
import {
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from "@mui/material";
import {
  Close,
  OpenInFull,
  AttachFile,
  MicOff,
  Send,
} from "@mui/icons-material";
import HistoryIcon from "assets/HistoryIcon";
import { RefreshIcon } from "assets/RefreshIcon";
import { IconChevronDown, IconChevronUp, IconSquareRoundedPlus } from "@tabler/icons-react";
import MenuClose from "assets/MenuClose";
import MenuOpen from "assets/MenuOpen";
import CustomMarkdown from "core/components/global/markdown/markdown";
import { fetchBaseUrl } from "core/utils/common";
import FullHistoryPanel from './agenticChatHistory';
import VoiceChat from 'core/components/global/input/voice/voiceChat';
import { FilePreviewCard } from 'core/components/global/input/inputBars/FilePreviewCard';



// Define types for messages and conversations
type MessageType = 'user' | 'ai';
type Message = {
  id: string;
  type: MessageType;
  content: string;
  timestamp: Date;
};

type Conversation = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  is_active: boolean;
  message_count: number;
  metadata: any;
};

type Task = {
  name: string;
  description: string;
  capabilities: {name: string; description: string}[];
  task_type?: string;
  supports_streaming?: boolean;
  endpointType?: string;
  endpointsuffix?: string;
};

const baseURL = fetchBaseUrl({port: 8000});

// Helper functions for date grouping
const formatDate = (date) => new Date(date);

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date) => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date) => {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return date > oneMonthAgo && !isPastWeek(date);
};

const groupConversationsByDate = (conversations: Conversation[]) => {
  const todayConversations: Conversation[] = [];
  const yesterdayConversations: Conversation[] = [];
  const pastWeekConversations: Conversation[] = [];
  const lastMonthConversations: Conversation[] = [];
  const olderConversations: Conversation[] = [];

  conversations.forEach((conversation) => {
    const conversationDate = formatDate(conversation.updated_at);

    if (isToday(conversationDate)) {
      todayConversations.push(conversation);
    } else if (isYesterday(conversationDate)) {
      yesterdayConversations.push(conversation);
    } else if (isPastWeek(conversationDate)) {
      pastWeekConversations.push(conversation);
    } else if (isPastMonth(conversationDate)) {
      lastMonthConversations.push(conversation);
    } else {
      olderConversations.push(conversation);
    }
  });

  return {
    todayConversations,
    yesterdayConversations,
    pastWeekConversations,
    lastMonthConversations,
    olderConversations,
  };
};

export const SocAiAgent = () => {
    const { categorizedConversations } = useAppSelector((state) => state.chatState);
    const [fullScreen, setFullScreen] = useState(false);
    const [viewMode, setViewMode] = useState("chat");
    const [searchType, setSearchType] = useState("kb");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [userPrompt, setUserPrompt] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);
    const theme = useTheme();
    
    // Conversation states
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [todayConversations, setTodayConversations] = useState<Conversation[]>([]);
    const [yesterdayConversations, setYesterdayConversations] = useState<Conversation[]>([]);
    const [pastWeekConversations, setPastWeekConversations] = useState<Conversation[]>([]);
    const [lastMonthConversations, setLastMonthConversations] = useState<Conversation[]>([]);
    const [olderConversations, setOlderConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
    const [highlightedConversation, setHighlightedConversation] = useState("");
    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [searchOption, setSearchOption] = useState("Chat");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showhistory, setShowhistory] = useState(false);
    const [hidden, setHidden] = useState<{ [id: string]: boolean }>({
        today: false,
        yesterday: true,
        pastWeek: true,
        lastMonth: true,
        older: true
    });
const [previewFile, setPreviewFile] = useState<File | null>(null);
    const { isVisible, hideChatbot } = useChatbot();
    const dispatch = useAppDispatch();
 useEffect(() => {
    dispatch(fetchConversationsThunk());
    fetchConversations();
  }, []);
  
  // Set default task
  useEffect(() => {
    // Initialize with the first task
    if (flywheelTasks.length > 0) {
      setSelectedTask(flywheelTasks[0]);
      setSearchOption(flywheelTasks[0].name);
    }
  }, []);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [conversationId, setConversationId] = useState<string | null>(null);
    // State for showing full history panel (will be implemented in a separate component)
    const [showFullHistory, setShowFullHistory] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    useEffect(() => {
      if (!conversationId) {
        setConversationId(uuidv4());
      }
    }, []);

    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [messages]);

    const handleLabelClick = (label) => {
        setSelectedLabel(label === selectedLabel ? null : label);
        
        // If clicking on a time period category, toggle its visibility
        if (['today', 'yesterday', 'pastWeek', 'lastMonth', 'older'].includes(label)) {
          setHidden(prev => ({
            ...prev,
            [label]: !prev[label]
          }));
        }

        if (label === "refresh") {
          fetchConversations();
            setIsRefreshing(true);
      console.log("fetching convos")
      setTimeout(() => {
        setIsRefreshing(false);
      }, 2000);
          dispatch(fetchConversationsThunk()); // Also fetch from Redux store
          console.log("fetching convos")
        }
        if (label === "new") {
          setMessages([]);
          setShowFullHistory(false);
          // Create a new conversation ID
          setConversationId(uuidv4());
          dispatch(clearCurrentChats());
        }
        if (label === "history") {
          setShowFullHistory(true);
          dispatch(fetchConversationsThunk());
        }
    };

    const fetchConversations = async () => {
      try {
        const response = await fetch(`${baseURL}/socai/api/context/conversations`, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'dev-key'
          }
        });
        
        const data = await response.json();
        console.log('Fetched conversations:', data);

        if (data && data.conversations) {
          setConversations(data.conversations);

          // Group conversations by date
          const {
            todayConversations,
            yesterdayConversations,
            pastWeekConversations,
            lastMonthConversations,
            olderConversations
          } = groupConversationsByDate(data.conversations);

          setTodayConversations(todayConversations);
          setYesterdayConversations(yesterdayConversations);
          setPastWeekConversations(pastWeekConversations);
          setLastMonthConversations(lastMonthConversations);
          setOlderConversations(olderConversations);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      }
    };

    const fetchConversationMessages = async (conversationId: string) => {
      try {
        const response = await fetch(
          `${baseURL}/socai/api/conversation/${conversationId}/messages`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': 'dev-key'
            }
          }
        );
        const data = await response.json();
        
        // Map them to your Message type
        const backendMessages = (data.messages || []).map((msg: any) => ({
          id: msg.id || `msg-${Date.now()}`,
          type: msg.role === "user" ? "user" : "ai",
          content: msg.content,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(backendMessages);
      } catch (error) {
        console.error('Error fetching conversation messages:', error);
        setMessages([
          {
            id: `error-${Date.now()}`,
            type: 'ai',
            content: 'Failed to load messages for this conversation.',
            timestamp: new Date()
          }
        ]);
      }
    };

    const handleSelectConversation = (conversation: Conversation) => {
      setHighlightedConversation(conversation.id);
      setSelectedConversation(conversation.id);
      fetchConversationMessages(conversation.id);
    };
   

    const handleCloseAgent = () => {
        hideChatbot();
    };

    

const postData = async () => {
  
     if (previewFile) {
     
    setMessages(prev => [
      ...prev,
      {
        id: `user-file-${Date.now()}`,
        type: 'user',
        content: `Uploaded file: ${previewFile.name}`,
        timestamp: new Date()
      }
    ]);

    try {
      // Read file as base64
      const toBase64 = (file: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            // Remove the data:*/*;base64, part
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
          reader.onerror = error => reject(error);
        });

      const file_content = await toBase64(previewFile);
      const filePayload = {
        file: {
          file_content,
          file_name: previewFile.name,
          mime_type: previewFile.type,
          file_size: previewFile.size
        },
        user_input: userPrompt,
        conversation_id: conversationId || uuidv4()
      };

      const response = await fetch(
        `${baseURL}/socai/api/upload/orchestrator?stream=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'dev-key'
          },
          body: JSON.stringify(filePayload)
        }
      );
setPreviewFile(null);
setUserPrompt('');
      if (!response.body) {
        throw new Error("No response body");
      }

      setMessages(prev => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: "",
          timestamp: new Date()
        }
      ]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          
          setMessages(prev => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].type === 'ai') {
                updated[i] = {
                  ...updated[i],
                  content: updated[i].content + chunk
                };
                break;
              }
            }
            
        return updated;
          });
      }
    }
    } catch (error) {
      setPreviewFile(null);
      setUserPrompt('');
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: 'ai',
        content: 'Sorry, there was an error processing your file upload.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
 
  }
   else{
  if (!userPrompt.trim()) return;
  // Add user message to the conversation
  const userMessage: Message = {
    id: `user-${Date.now()}`,
    type: 'user',
    content: userPrompt,
    timestamp: new Date()
  };
  setMessages(prev => [...prev, userMessage]);

  // Clear input field
  setUserPrompt('');

  // Set loading state
  setIsLoading(true);

  try {
    // Use the correct API endpoint based on the selected task
    let endpoint = `${baseURL}/socai/api/serve`;
    if (selectedTask?.name === "Security Analysis") {
      endpoint = `${baseURL}/socai/api/workflow/security_analysis`;
    }

    const response = await fetch(endpoint + '?stream=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': 'dev-key'
      },
      body: JSON.stringify({
        user_input: userMessage.content,
        conversation_id: conversationId
      })
    });

    if (!response.body) {
      throw new Error("No response body");
    }

    // Add an empty AI message for streaming
    setMessages(prev => [
      ...prev,
      {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: "",
        timestamp: new Date()
      }
    ]);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        setMessages(prev => {
          const updated = [...prev];
          // Find the last AI message (the one we just added)
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].type === 'ai') {
              updated[i] = {
                ...updated[i],
                content: updated[i].content + chunk
              };
              break;
            }
          }
          return updated;
        });
      }
    }

    setIsLoading(false);
  } catch (error) {
    console.error('Error:', error);

    // Add error message to conversation
    const errorMessage: Message = {
      id: `error-${Date.now()}`,
      type: 'ai',
      content: 'Sorry, there was an error processing your request.',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMessage]);
    setIsLoading(false);
  }
}
};

// ...existing code...

const FileUploadButton = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

     const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (file) {
 setPreviewFile(file);
  }
 
};
      const triggerFileInput = () => {
        fileInputRef.current?.click();
      };

      return (
        <div>
          <AttachFile
            className="cursor-pointer"
            onClick={triggerFileInput}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      );
    };

    // Define the Flywheel-specific task options
    const flywheelTasks: any = [
      {
        name: "Chat",
        description: "Ask questions over our agents",
        capabilities: [],
        endpointType: "api",
        endpointsuffix: "serve",
        supports_streaming: true
      },
      {
        name: "Security Analysis",
        description: "comprehensive security incident analysis and reporting",
        capabilities: [],
        endpointType: "workflow",
        endpointsuffix: "security_analysis",
        supports_streaming: true
      }
    ];

    // Chat + Security Analysis task
    
    // Use the Flywheel-specific tasks instead of the fetched ones
    const filteredTasks = flywheelTasks;
     const handleDataFromChild = (data) => {
    setUserPrompt((prevPrompt) => `${prevPrompt} ${data}`.trim());
  }

    if (!isVisible) return null;
    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${theme.name === "dark-theme" ? "backdrop-blur-md bg-gray-700 bg-opacity-10" : "backdrop-blur-md bg-purple-300 bg-opacity-40"}`}>
          <div
            className={`border border-black shadow-lg transition-all duration-300 ease-in-out ${fullScreen ? "w-screen h-screen" : "w-[90vw] h-[89vh]"} flex`}
          >
            {/* Sidebar */}
            <div
              className={`transition-all py-2 duration-300 ${theme.name === "dark-theme" ? "bg-[#212121] text-white" : "bg-[#ffffff] text-black"} ${!sidebarCollapsed ? "w-[300px]" : "w-[4vw]"}`}
            >
              <div className="">
                <div className="p-4 d-flex flex-column justify-content-start">
                  <div className="cursor-pointer mb-4" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                    {sidebarCollapsed ? <MenuClose /> : 
                    <span className="d-flex flex-row items-center gap-2 mb-4 cursor-pointer">
                      <MenuOpen /><h3 className="text-base">Conversation</h3>
                    </span>}
                  </div>
                  <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => handleLabelClick("refresh")}>
                <span className={isRefreshing ? "animate-spin" : ""}>
                  <RefreshIcon />
                </span>
                    {!sidebarCollapsed && <h3 className="text-base">Refresh Conversations</h3>}
                  </div>
                  <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => handleLabelClick("new")}><IconSquareRoundedPlus />
                    {!sidebarCollapsed && <h3 className="text-base">New Conversation</h3>}
                  </div>
                  <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => handleLabelClick("history")}><HistoryIcon />
                    {!sidebarCollapsed && <h3 className="text-base">Full History</h3>}
                  </div>
                </div>

                {!sidebarCollapsed && (
                  <div
                    className="px-1 hide-on-short-menu"
                    style={{
                      overflowY: "auto",
                      position: "relative",
                      height: fullScreen
                        ? "calc(-338.8px + 110vh)"
                        : "calc(-338.8px + 99vh)"
                    }}
                  >
                    <div className="d-flex flex-column cursor-pointer w-100 p-4 space-y-4">
                      {/* Today section */}
                      <div className="d-flex flex-column">
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => handleLabelClick("today")}
                        >
                          <span className="text-base">Today</span>
                          {hidden.today ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                        </div>
                        {!hidden.today && todayConversations.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {todayConversations.map((conversation) => (
                              <div
                                key={conversation.id}
                                className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${theme.name === "dark-theme"
                                  ? "hover:bg-gray-700 text-white"
                                  : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                    ? theme.name === "dark-theme"
                                      ? "bg-gray-700 text-white"
                                      : "bg-purple-100 text-black"
                                    : ""
                                  }`}
                                onClick={() => handleSelectConversation(conversation)}
                              >
                                {conversation.title}
                              </div>
                            ))}
                          </div>
                        )}
                        {!hidden.today && todayConversations.length === 0 && (
                          <div className="ml-4 mt-1 p-2 text-sm text-gray-500">
                            No conversations today
                          </div>
                        )}
                      </div>

                      {/* Yesterday section */}
                      <div>
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => handleLabelClick("yesterday")}
                        >
                          <span className="text-base">Yesterday</span>
                          {hidden.yesterday ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                        </div>
                        {!hidden.yesterday && yesterdayConversations.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {yesterdayConversations.map((conversation) => (
                              <div
                                key={conversation.id}
                                className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${theme.name === "dark-theme"
                                  ? "hover:bg-gray-700 text-white"
                                  : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                    ? theme.name === "dark-theme"
                                      ? "bg-gray-700 text-white"
                                      : "bg-purple-100 text-black"
                                    : ""
                                  }`}
                                onClick={() => handleSelectConversation(conversation)}
                              >
                                {conversation.title}
                              </div>
                            ))}
                          </div>
                        )}
                        {!hidden.yesterday && yesterdayConversations.length === 0 && (
                          <div className="mt-1 p-2 text-sm text-gray-500">
                            No conversations yesterday
                          </div>
                        )}
                      </div>

                      {/* Past Week section */}
                      <div>
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => handleLabelClick("pastWeek")}
                        >
                          <span className="text-base">Past Week</span>
                          {hidden.pastWeek ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                        </div>
                        {!hidden.pastWeek && pastWeekConversations.length > 0 && (
                          <div className="mt-1 space-y-1 max-h-48 overflow-y-auto pr-1">
                            {pastWeekConversations.map((conversation) => (
                              <div
                                key={conversation.id}
                                className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${theme.name === "dark-theme"
                                  ? "hover:bg-gray-700 text-white"
                                  : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                    ? theme.name === "dark-theme"
                                      ? "bg-gray-700 text-white"
                                      : "bg-purple-100 text-black"
                                    : ""
                                  }`}
                                onClick={() => handleSelectConversation(conversation)}
                              >
                                {conversation.title}
                              </div>
                            ))}
                          </div>
                        )}
                        {!hidden.pastWeek && pastWeekConversations.length === 0 && (
                          <div className="mt-1 p-2 text-sm text-gray-500">
                            No conversations from past week
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className={`flex-1 flex flex-col ${theme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"} overflow-hidden`}>
              {/* Header */}
              <div className={`flex justify-end ${theme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#faf5ff]"} px-6 pt-2 pb-2`}>
                <div className="space-x-2 flex items-center">
                  <IconButton onClick={() => setFullScreen(!fullScreen)}>
                    <OpenInFull className="text-purple-600" />
                  </IconButton>
                  <IconButton onClick={handleCloseAgent}>
                    <Close className="text-purple-600" />
                  </IconButton>
                </div>
              </div>

              {/* Body */}
               {viewMode === "chat" && (
              <div className={`flex-1 px-6 py-2 pb-2 space-y-4 overflow-y-auto`}>
                {showFullHistory ?(
                <FullHistoryPanel
                  isOpen={showFullHistory}
                  onClose={() => setShowFullHistory(false)}
                  conversations={conversations}
                  apptheme={theme}
                  onSelectConversation={fetchConversationMessages}
                  fetchConversations={fetchConversations}
                />  ) : (
                <>
                <div className={`z-2 t-0 sticky px-10 pt-6 pb-2 ${theme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"} sticky !important top-[-9px] !important`}>
                  <p
                    className={`font-bold mb-2 ${theme.name === "dark-theme" ? "text-white" : "text-black"}`}
                    style={{ textTransform: "none" }}
                  >
                    SEARCH AGENT SUITE
                  </p>
                  <FormControl className={`${theme.name === "dark-theme" ? "text-white" : "text-black"}`} component="fieldset">
                    <RadioGroup
                      row
                      value={searchOption}
                      onChange={e => {
                        setSearchOption(e.target.value);
                        const task = filteredTasks.find((t: Task) => t.name === e.target.value);
                        setSelectedTask(task || null);
                      }}
                    >
                      {filteredTasks.map((task: Task) => (
                        <FormControlLabel
                          key={task.name}
                          value={task.name}
                          control={
                            <Radio
                              sx={{
                                color: "#a100ff",
                                "&.Mui-checked": { color: "#a100ff" }
                              }}
                            />
                          }
                          label={task.name.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                          sx={{
                            marginRight: 4,
                            ".MuiFormControlLabel-label": {
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              letterSpacing: "0.02em"
                            }
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  
                </div>

                {/* Show messages */}
                <div className="p-6 z-1">
                  <div className="transition-column mt-3 ml-[5.5rem] me-3 col-md-10 messages d-flex flex-column h-full overflow-auto flex-grow gap-[1.5rem]">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-lg shadow max-w-[35vw] ${msg.type === 'user'
                          ? `ml-auto ${theme.name === "dark-theme" ? "bg-[#212121] text-white" : "bg-purple-100 text-black"}`
                          : `mr-auto ${theme.name === "dark-theme" ? "bg-gray-600 text-white" : "bg-purple-100 text-black"}`
                          }`}
                      >
                        <p className="text-sm">
                          {typeof msg.content === 'string' ? 
                            <CustomMarkdown content={msg.content} showEditButton={false} /> : 
                            JSON.stringify(msg.content)}
                        </p>
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}

                  
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                </>
                
                )}
</div>
              )}

              {/* Footer */}
              {/* Footer Chat Input */}
              <div className={`${theme.name === "dark-theme" ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]" : ""} p-4`}>
                 {previewFile && (
                           <FilePreviewCard
                             file={previewFile}
                             onRemove={() => setPreviewFile(null)}
                           />
                         )}
                <div className={`flex items-center gap-2 ${theme.name === "dark-theme" ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]" : "bg-[#faf5ff]"} px-1 py-1 rounded-lg border mb-3 ml-5`}>
                  <input
                    placeholder="Type your message..."
                    className={`flex-1 outline-none border-none bg-transparent p-2 ${theme.name === "dark-theme" ? "text-white !important" : "text-black"}`}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    value={userPrompt}
                    onKeyPress={(e) => e.key === 'Enter' && postData()}
                  />
                  <IconButton className={`${theme.name === "dark-theme" ? "text-white !important" : "text-black"}`}>
                    <FileUploadButton />
                  </IconButton>
                  <IconButton className={`${theme.name === "dark-theme" ? "text-white !important" : "text-black"}`}>
                     <VoiceChat sendDataToParent={handleDataFromChild} sendMessageByVoice={postData}/>
                  </IconButton>
                  <IconButton onClick={postData} className={`${theme.name === "dark-theme" ? "text-white !important" : "text-black"}`}>
                    <Send />
                  </IconButton>
                </div>
                
                          
              </div>
              <div className={`text-right mt-2 ${theme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#F4EDF9]"}`}>
                <Button variant="text" color="primary" onClick={handleCloseAgent}>
                  Close Agent Suite
                </Button>
              </div>
            </div>
          </div>
        </div>
    );
}


